import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Financial Content) ---
import {
    // Icons mapped to the 5 financial services:
    faCalculator,         // 1. Day-to-Day Accounting
    faBalanceScale,       // 2. Auditing (Alternative: faSearchDollar)
    faArrowTrendUp,       // 3. Financial Growth Strategy (Alternative: faChartLine)
    faScissors,           // 4. Cost Reduction (Alternative: faPiggyBank)
    faMagnifyingGlassChart, // 5. Assessment Studies (Alternative: faChartPie)
    // Icons for Methodology/Results/Why Choose (Optional, can use generic ones)
    faBullseye,           // Methodology/Results Focus
    faThumbsUp,           // Why Choose Us/Trust
    // Keep other potentially useful icons if needed, or remove if strictly unused
    // Icons likely unused from previous versions:
    /*
    faClipboardCheck, faChartLine, faBullhorn, faLaptop, faHandshake, faDatabase,
    faFileMedical, faClipboardList, faChalkboardTeacher
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.3 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "Specialized Services" (Features Part 1)
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "Specialized Services" (Detailed Planning Part 2)
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // For Methodology Section
    const [title6Ref, title6Visible] = useIntersectionObserverTitle(titleObserverOptions); // For Results Section
    const [title7Ref, title7Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "Why Choose Us" Section

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Keeping original hero image, can be changed if needed
    const heroBgImage = 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Changed to a more financial/tech looking image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Comprehensive Hospital Financial Management</h1>
            <p className="cliniqor-sub-heading">
              Elevating Healthcare Institutions Through Strategic Fiscal Excellence
            </p>
            <p className="cliniqor-intro-text">
              Effective financial management is crucial for ensuring hospitals maintain stability and offer superior care. Cliniqor delivers advanced financial solutions that optimize fiscal health, streamline operations, and guarantee judicious resource allocation for sustainable growth and success.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO SECTION --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>Our Approach: Integrated Financial Expertise</h3>
                     <p>Our approach to hospital financial management integrates innovative strategies, deep financial expertise, and a patient-centered perspective. We aim to provide unparalleled support in managing hospital operations, ensuring financial growth, and maintaining fiscal resilience while safeguarding the highest standards of patient care.</p>
                     <p>With Cliniqor, hospitals benefit from meticulously crafted financial solutions tailored to their unique needs, driving profitability, reducing inefficiencies, and enabling effective resource management.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
                 {/* Removed old quote box */}
             </div>
          </div>
        </section>

        {/* --- SPECIALIZED SERVICES PART 1 (Points 1-3) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Our Specialized Hospital Financial Management Services</h2>
              <p>Addressing dynamic needs with a focus on efficiency, growth, and compliance.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained - Adjust grid columns if needed */}
              {/* --- NEW FEATURE CARDS (Mapped from sections 1-3) --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCalculator} /></div>
                    <h4>1. Day-to-Day Accounting Management</h4>
                    <p>Mastering financial operations with precision, transparency, tracking, and reporting.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBalanceScale} /></div>
                    <h4>2. Internal and External Auditing</h4>
                    <p>Ensuring accountability, compliance, financial integrity, and risk mitigation.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faArrowTrendUp} /></div>
                    <h4>3. Financial Growth Strategy</h4>
                    <p>Unlocking opportunities for sustainable revenue, cost structuring, and long-term success.</p>
                </div>
              </div>
              {/* Only 3 items in this section */}
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>


        {/* --- SPECIALIZED SERVICES PART 2 (Points 4-5) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Optimization and Assessment Strategies</h2>
               <p>Maximizing resource efficiency and providing detailed financial diagnostics.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-planning-items-wrapper"> {/* Kept existing wrapper class - Adjust layout if needed for only 2 items */}
                 {/* --- NEW PLANNING ITEMS (Mapped from sections 4-5) --- */}
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faScissors} /></div>
                    <div className="cliniqor-content"><h4>4. Cost Reduction Strategies</h4> <p>Optimizing operational expenses, workflows, supply chains, and resource utilization.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faMagnifyingGlassChart} /></div>
                    <div className="cliniqor-content"><h4>5. Assessment Studies</h4> <p>Detailed financial diagnostics, KPI evaluation, benchmarking, and strategic recommendations.</p> </div>
                 </div>
                 {/* Only 2 items in this section */}
                 {/* --- END NEW PLANNING ITEMS --- */}
            </div>
          </div>
        </section>

        {/* --- METHODOLOGY SECTION --- */}
        <section className="cliniqor-section cliniqor-section-bg-light"> {/* Reusing a background style */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Cliniqor’s Financial Methodology</h2>
                 <p>A Holistic Approach to Financial Excellence</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up"> {/* Simple text container */}
                   <p>At Cliniqor, our methodology is grounded in a comprehensive understanding of both the healthcare and financial sectors. We follow a rigorous, results-driven approach emphasizing tailored solutions, continuous monitoring, and effective strategy implementation designed to foster growth, sustainability, and operational efficiency.</p>
                   <p>Our process includes data-driven financial analysis, collaborative strategic development with leadership, ongoing performance monitoring via KPIs, and implementing scalable solutions designed for growth while maintaining financial efficiency.</p>
              </div>
           </div>
        </section>

        {/* --- RESULTS SECTION --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title6Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title6Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Delivering Tangible Results</h2>
                 <p>Maximizing Financial Performance for Optimal Patient Care</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up"> {/* Simple text container */}
                   <p>By partnering with Cliniqor, hospitals gain access to unparalleled financial expertise, actionable insights, and customized strategies that enhance operational efficiency and optimize resource allocation. Our solutions are designed to ensure that your hospital remains financially sustainable while delivering the highest standards of patient care.</p>
                   <p>Key deliverables include enhanced financial performance, optimized resource utilization, cost-effective solutions without compromising quality, and crafting financial strategies that guarantee sustainable long-term growth and profitability.</p>
              </div>
           </div>
        </section>

        {/* --- WHY CHOOSE US SECTION --- */}
        <section className="cliniqor-section cliniqor-section-bg-dark cliniqor-why-choose-us-section"> {/* Reusing dark background */}
           <div className="cliniqor-container">
              <div ref={title7Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title7Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Why Choose Cliniqor?</h2>
                 <p>Your Partner for Hospital Financial Management Needs</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up" style={{ textAlign: 'center', color: '#ffffff' /* Adjust color for dark bg */ }}> {/* Centered text */}
                   <p style={{color: '#ffffff'}}>Choosing Cliniqor means partnering with a trusted advisor committed to driving your hospital’s financial success. With our deep expertise in both healthcare operations and financial management, we help hospitals navigate the complexities of the healthcare environment while optimizing their financial performance.</p>
                   <p style={{color: '#fff'}}>From improving cash flow management to creating effective cost-reduction strategies, Cliniqor provides the tools and expertise needed to ensure your hospital’s long-term sustainability and operational brilliance.</p>
                    {/* Optional Icon */}
                   {/* <div style={{ marginTop: '20px', fontSize: '2em' }}>
                       <FontAwesomeIcon icon={faThumbsUp} />
                   </div> */}
              </div>
           </div>
        </section>


      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Lead Your Hospital to Financial Success</h3>
          <p>Transform your hospital’s financial operations with Cliniqor. Contact us today to learn how we can help optimize your financial strategies, streamline operations, and drive sustainable growth.</p>
          {/* --- END UPDATED FOOTER CONTENT --- */}
          <a href="#contact" className="cliniqor-cta-button">Request a Consultation</a> {/* Button kept */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;